import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { compare } from 'fast-json-patch'

import { useCreateCourt } from '../../../../../hooks/court/useCreateCourt'
import { useUpdateCourt } from '../../../../../hooks/court/useUpdateCourt'

import { Card } from '../../../../card/Card'
import { ClubSummary } from './clubSummary/ClubSummary'
import { TextInput } from '../../../../input/TextInput'
import FormSection from '../../../../formSection/FormSection'
import { SelectOptionInput } from '../../../../input/selectOptionInput/SelectOptionInput'
import FormSectionActions from '../../../../formSection/formSectionActions/FormSectionActions'

import { useApp } from '../../../../../contexts/AppContext'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import {
  CourtType,
  ClubModel,
  CourtModel,
} from '../../../../../types/court/court'

type CourtEditProps = {
  court: CourtModel | null
  clubs: ClubModel[]
  clubsLoading: boolean
  isEditMode: boolean
  onLoadMoreClubs: () => void
  onCourtUpdated?: (court: CourtModel) => void
}

type CourtFormDetails = Omit<CourtModel, 'courtType'> & {
  courtType: CourtType | ''
}

type CourtField = 'name' | 'courtType'

const toHourMinute = (time?: string) => time?.slice(0, 5) ?? ''

const toCourtFormDetails = (
  court?: Partial<CourtModel> | null
): CourtFormDetails => ({
  id: court?.id,
  name: court?.name ?? '',
  courtType: court?.courtType ?? '',
  club: {
    id: court?.club?.id,
    name: court?.club?.name ?? '',
    openTime: toHourMinute(court?.club?.openTime),
    closeTime: toHourMinute(court?.club?.closeTime),
    freeCancellation: court?.club?.freeCancellation ?? 0,
    startIncrementMinutes: court?.club?.startIncrementMinutes ?? 30,
    sessionDurations: court?.club?.sessionDurations ?? [],
    maxReschedules: court?.club?.maxReschedules ?? 0,
    passExtensionDays: court?.club?.passExtensionDays ?? 90,
    timeZone: court?.club?.timeZone,
    address: {
      id: court?.club?.address?.id,
      county: court?.club?.address?.county ?? '',
      city: court?.club?.address?.city ?? '',
      street: court?.club?.address?.street ?? '',
      number: court?.club?.address?.number ?? '',
      floor: court?.club?.address?.floor ?? '',
      apartment: court?.club?.address?.apartment ?? '',
      postalCode: court?.club?.address?.postalCode ?? '',
    },
  },
})

export const CourtEdit = ({
  court,
  clubs,
  clubsLoading,
  isEditMode,
  onLoadMoreClubs,
  onCourtUpdated,
}: CourtEditProps) => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { t } = useTranslation('courts')
  const [details, setDetails] = useState<CourtFormDetails>(() =>
    toCourtFormDetails(court)
  )
  const [initialDetails, setInitialDetails] = useState<CourtFormDetails>(() =>
    toCourtFormDetails(court)
  )

  const {
    createCourt,
    loading: createLoading,
    error: createError,
    success: createSuccess,
    resetState: resetCreateState,
  } = useCreateCourt()

  const {
    updateCourt,
    loading: updateLoading,
    error: updateError,
    success: updateSuccess,
    resetState: resetUpdateState,
  } = useUpdateCourt()

  const loading = createLoading || updateLoading

  useEffect(() => {
    const model = toCourtFormDetails(court)
    setInitialDetails(model)
    setDetails(model)
  }, [court, isEditMode])

  useEffect(() => {
    const error = createError || updateError
    const success = createSuccess || updateSuccess

    if (error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: error,
      })
    }

    if (success) {
      showBanner({
        kind: BannerKind.OK,
        msg: success,
      })
    }

    resetState()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, createSuccess, updateError, updateSuccess])

  const resetState = () => {
    resetCreateState()
    resetUpdateState()
  }

  const patch = useMemo(() => {
    const operations = compare(initialDetails, details).filter(
      operation => !operation.path.startsWith('/club/')
    )

    if (
      details.club.id !== undefined &&
      initialDetails.club.id !== details.club.id
    ) {
      operations.push({
        op: initialDetails.club.id === undefined ? 'add' : 'replace',
        path: '/club/id',
        value: details.club.id,
      })
    }

    return operations
  }, [initialDetails, details])

  const isDirty = patch.length > 0
  const formIsValid =
    Boolean(details.name.trim()) &&
    details.courtType !== '' &&
    details.club.id !== undefined

  const courtTypeOptions = useMemo(
    () =>
      Object.values(CourtType).map(type => ({
        value: type,
        label: t(`courts.type.${type}`),
      })),
    [t]
  )

  const clubOptions = clubs.map(club => ({
    value: String(club.id),
    label: club.name,
  }))

  const handleChange = (name: string, value: string, _isValid?: boolean) => {
    if (name === 'clubId') {
      const selectedClub = clubs.find(club => String(club.id) === value)

      if (!selectedClub) {
        return
      }

      setDetails(previous => ({
        ...previous,
        club: {
          ...selectedClub,
          openTime: toHourMinute(selectedClub.openTime),
          closeTime: toHourMinute(selectedClub.closeTime),
        },
      }))
      return
    }

    const field = name as CourtField
    const nextValue = field === 'courtType' ? (value as CourtType) : value

    setDetails(previous => ({
      ...previous,
      [field]: nextValue,
    }))
  }

  const handleSave = async () => {
    if (loading || !formIsValid) {
      return
    }

    if (!isEditMode) {
      const createdCourt = await createCourt({
        ...details,
        courtType: details.courtType as CourtType,
      })

      if (createdCourt?.id === undefined) {
        return
      }

      const savedDetails = toCourtFormDetails(createdCourt)
      setInitialDetails(savedDetails)
      setDetails(savedDetails)
      onCourtUpdated?.(createdCourt)
      navigate('/m/courts', { replace: true })
      return
    }

    if (details.id === undefined || patch.length === 0) {
      return
    }

    const updatedCourt = await updateCourt(details.id, patch)

    if (!updatedCourt) {
      return
    }

    const savedDetails = toCourtFormDetails(updatedCourt)
    setInitialDetails(savedDetails)
    setDetails(savedDetails)
    onCourtUpdated?.(updatedCourt)
    navigate('/m/courts', { replace: true })
  }

  const handleReset = () => {
    setDetails(initialDetails)
  }

  return (
    <div className="col-12">
      <Card>
        <FormSection
          title={t('courts.edit.details.title')}
          subtitle={t('courts.edit.details.subtitle')}
          actions={
            <FormSectionActions
              isDirty={isDirty}
              isValid={formIsValid}
              loading={loading}
              onSave={handleSave}
              onCancel={handleReset}
              saveLabel={t(
                isEditMode ? 'courts.edit.save-btn' : 'courts.new.save-btn'
              )}
              resetLabel={t('courts.edit.reset-btn')}
            />
          }
        >
          <div className="court-edit">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <TextInput
                  name="name"
                  label={t('courts.edit.fields.name')}
                  value={details.name}
                  onChange={handleChange}
                  errorMessage={t('courts.edit.validation.required')}
                  required
                />
              </div>
              <div className="col-12 col-md-6">
                <SelectOptionInput
                  name="courtType"
                  label={t('courts.edit.fields.type')}
                  value={details.courtType}
                  list={courtTypeOptions}
                  onChange={handleChange}
                  errorMessage={t('courts.edit.validation.required')}
                  required
                />
              </div>
              <div className="col-12">
                <div className="club">
                  <div className="club-header">
                    <h3 className="club-title">
                      {t('courts.edit.configuration.title')}
                    </h3>
                    <p className="club-subtitle">
                      {t('courts.edit.configuration.subtitle')}
                    </p>
                  </div>
                  <SelectOptionInput
                    name="clubId"
                    label={t('courts.edit.fields.configuration')}
                    value={
                      details.club.id !== undefined
                        ? String(details.club.id)
                        : ''
                    }
                    list={clubOptions}
                    disabled={loading || (clubsLoading && clubs.length === 0)}
                    loading={clubsLoading}
                    onLoadMore={onLoadMoreClubs}
                    onChange={handleChange}
                    errorMessage={t('courts.edit.validation.required')}
                    required
                  />
                  <ClubSummary club={details.club} />
                </div>
              </div>
            </div>
          </div>
        </FormSection>
      </Card>
    </div>
  )
}
